import { clsx, type ClassValue } from 'clsx'
import { DateTime } from 'luxon'
import { twMerge } from 'tailwind-merge'

/**
 * Merges Tailwind CSS classes intelligently, resolving conflicts.
 *
 * Combines `clsx` for conditional class composition with `tailwind-merge`
 * to intelligently resolve conflicting Tailwind utilities.
 *
 * @param inputs - Class names as strings, arrays, objects, or falsy values
 * @returns Merged class string with Tailwind conflicts resolved
 *
 * @example
 * // Basic usage
 * cn('px-2 py-1', 'px-4') // Returns "py-1 px-4" (px-4 overrides px-2)
 *
 * @example
 * // Conditional classes
 * cn('text-red-500', isActive && 'text-blue-500')
 * // Returns "text-blue-500" if isActive is true
 *
 * @example
 * // Mixed input types
 * cn(['bg-white', 'rounded'], { 'shadow-lg': isError })
 */
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export const extractErrorMessage = (
	e: any,
	msg: string = 'Unexpected error, please try again'
): string => {
	// First check for Laravel validation errors
	if (e?.response?.data?.errors) {
		const errorKeys = Object.keys(e.response.data.errors)

		let key: string | null = null
		if (errorKeys.length > 0) {
			key = errorKeys[0]
		}

		if (key) {
			return e.response.data.errors[key][0]
		}

		return msg
	}

	// Use default error message from API if nothing was found before
	if (e?.response?.data?.message) {
		return e.response.data.message
	}

	// Use the default message as final resource
	return msg
}

/**
 * Capitalizes the first letter of a string
 * @param str - The string to capitalize
 * @returns String with first letter uppercase and rest unchanged
 */
export const capitalizeFirstLetter = (str: string): string => {
	if (!str || str.length === 0) return str
	return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Returns the last seen text for a zone
 */
export const lastSeenText = (
	lastSeen: DateTime,
	defaultText: string = 'N/A'
): string => {
	if (!lastSeen) return defaultText

	// If the last seen date is more than 1 year ago, return the date in the format of dd LLL, yyyy
	if (lastSeen.diffNow().negate().as('years') > 1) {
		return lastSeen.toFormat('dd LLL, yyyy')
	}
	// If the last seen date is less than 1 year ago, return the date in the format of dd LLL, HH:mm
	return lastSeen.toFormat('dd LLL, HH:mm')
}

// Helper function to format duration from seconds into hours and minutes
export const formatDuration = (seconds: number): string => {
	const hours = Math.floor(seconds / 3600)
	const minutes = Math.floor((seconds % 3600) / 60)
	if (hours > 0) {
		return `${hours}h ${minutes}m`
	}
	return `${minutes}m`
}
